import clsx from "clsx";
import React, { ReactNode } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import { styled } from "@mui/material/styles";
import { Button as UIButton, Text } from ".";
import { theme } from "$utils/theme/bataillog";

interface ModalProps extends DialogProps {
  title?: string;
  textValidate?: string;
  noCancelButton?: boolean;
  textCancel?: string;
  onCancel: () => void;
  onValidate?: () => void;
  children?: ReactNode;
  className?: string;
  isLoading?: boolean;
  step?: number;
  entrepotsSelectedFile?: any;
  onCsvExported?: () => void;
  onExportClick?: () => void;
  onConfirm?: any;
  /**
   * Prevent the user from closing the modal by clicking outside or by pressing the escape key
   */
  disableExternalClosing?: boolean;
  /** Override the action of the X (close) button independently from onCancel */
  onCloseButton?: () => void;
}

// Custom styled DialogTitle that renders our own content
const CustomDialogTitle = styled(DialogTitle)({
  margin: 0,
  padding: "15px 50px 15px 10px",
  textAlign: "center",
  position: "relative",
  "& .MuiTypography-root": {
    color: "#030f85",
    fontWeight: "bold",
    fontSize: 25,
  },
});

const CloseButton = styled(IconButton)({
  position: "absolute",
  right: 1,
  top: "50%",
  transform: "translateY(-50%)",
  color: "#2D2D2D",
});

const StyledDialogContent = styled(DialogContent)({
  padding: 10,
  borderBottom: "none",
});

const StyledDialogActions = styled(DialogActions)({
  margin: 0,
  padding: 10,
  display: "flex",
  justifyContent: "space-between",
});

// Custom DialogTitle component
interface CustomDialogTitleProps {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const CustomizedDialogTitle = (props: CustomDialogTitleProps) => {
  const { children, onClose, ...other } = props;
  return (
    <CustomDialogTitle {...other}>
      {typeof children === "string" ? (
        children
      ) : (
        <Text type="h2">{children}</Text>
      )}
      {onClose ? (
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      ) : null}
    </CustomDialogTitle>
  );
};

function Modal({
  title = "",
  className = "",
  textCancel = "Annuler",
  textValidate = "Supprimer",
  onCancel,
  onValidate,
  open,
  children,
  isLoading = false,
  noCancelButton = false,
  step,
  onCsvExported,
  onExportClick,
  entrepotsSelectedFile: _entrepotsSelectedFile,
  onConfirm,
  disableExternalClosing = false,
  onCloseButton,
  ...modalProps
}: ModalProps) {
  const classes = useStyles();
  const saveText = isLoading ? `${textValidate}...` : textValidate;

  const handleOnClose = (event: any, reason: string) => {
    if (
      !disableExternalClosing ||
      !["escapeKeyDown", "backdropClick"].includes(reason)
    ) {
      onCancel();
    }
  };

  return (
    <Dialog
      onClose={handleOnClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      {...modalProps}
      className={clsx(classes.modal, className)}
    >
      <CustomizedDialogTitle
        id="customized-dialog-title"
        onClose={onCloseButton ?? onCancel}
      >
        {title}
      </CustomizedDialogTitle>
      <StyledDialogContent dividers>{children}</StyledDialogContent>
      {onConfirm && (
        <StyledDialogActions className={clsx(classes.centerData)}>
          {!noCancelButton && (
            <Button
              onClick={onCancel}
              variant="outlined"
              style={{ textTransform: "none" }}
            >
              {textCancel}
            </Button>
          )}
          <UIButton onClick={onConfirm} color="primary" disabled={isLoading}>
            {saveText}
          </UIButton>
        </StyledDialogActions>
      )}
      {onValidate && (
        <StyledDialogActions className={clsx(classes.centerData)}>
          {!noCancelButton && (
            <Button
              onClick={onCancel}
              variant="outlined"
              style={{ textTransform: "none" }}
            >
              {textCancel}
            </Button>
          )}
          {step === 0 && onCsvExported && onExportClick && (
            <UIButton
              variant="contained"
              color="primary"
              className={classes.btnRight}
              onClick={onExportClick}
              disabled={isLoading}
            >
              <Upload2LineIcon />
              {saveText}
            </UIButton>
          )}
          {step !== 0 && (
            <UIButton
              type="button"
              onClick={onValidate}
              color="primary"
              disabled={isLoading}
              startIcon={
                isLoading && <CircularProgress color="inherit" size={16} />
              }
            >
              {saveText}
            </UIButton>
          )}
        </StyledDialogActions>
      )}
    </Dialog>
  );
}

const useStyles = createUseStyles(() => ({
  modal: {
    display: "flex",
    padding: theme.spacing?.(1) || 8,
    alignItems: "center",
    justifyContent: "center",
  },
  paperModal: {
    flex: "0 1 500px",
    display: "flex",
    borderRadius: 3,
    padding: [5, 10],
  },
  centerData: {
    justifyContent: "center !important",
  },
  btnRight: {
    "&.MuiButton-root": {
      marginLeft: 20,
    },
  },
}));

export default Modal;
